'use client';

/**
 * CriticalBanner — one-line summary at top of the Architecture tab.
 * Click DETAILS to expand inline (does NOT navigate away).
 */

import { useState } from 'react';
import type { ArchitectureState } from '@/lib/architecture-state';

interface Props {
  state: ArchitectureState;
}

export function CriticalBanner({ state }: Props): React.JSX.Element | null {
  const [open, setOpen] = useState(false);
  if (!state.criticalCount || state.criticalCount === 0) return null;

  const reasons: string[] = state.criticalReasons ?? [];

  return (
    <div
      role="alert"
      className="rounded-md border border-red-400/60 bg-red-400/10"
    >
      <div className="px-3 py-2 flex items-center gap-3 flex-wrap">
        <span
          className="w-1.5 h-1.5 rounded-full bg-red-400 shadow-[0_0_6px_rgba(248,113,113,0.9)] animate-pulse flex-shrink-0"
          aria-hidden
        />
        <span className="text-[10px] font-semibold uppercase tracking-wide text-red-200">
          {state.criticalCount} critical
        </span>
        <span className="text-[11px] text-red-100/90">
          {reasons.length > 0 ? reasons.join(' · ') : 'See sections below'}
        </span>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="ml-auto text-[10px] uppercase tracking-wide text-red-200 hover:text-white underline-offset-2 hover:underline"
        >
          {open ? 'hide' : 'details'}
        </button>
      </div>

      {open && (
        <div className="border-t border-red-400/30 px-3 py-2 text-[11px] text-red-100/80 space-y-1">
          {reasons.length === 0 && <div>No additional detail available.</div>}
          {reasons.map((r, i) => (
            <div key={i} className="font-mono">• {r}</div>
          ))}
        </div>
      )}
    </div>
  );
}
